import { z } from "zod";

/** Senha é o único fator do login, então vale a régua do NIST para esse caso (SP 800-63B): tamanho
 *  em vez de regra de composição. Frase longa passa; maiúscula, número e símbolo não são exigidos. */
export const SENHA_MIN = 15;
/** O bcrypt ignora o que passa de 72 bytes: aceitar mais daria a impressão de que conta. */
export const SENHA_MAX_BYTES = 72;

const SEQUENCIAS = ["0123456789", "abcdefghijklmnopqrstuvwxyz", "qwertyuiopasdfghjklzxcvbnm", "1q2w3e4r5t6y7u8i9o0p"];
const PALAVRAS_DO_CONTEXTO = ["dentalbook", "senha", "password"];

const normalizar = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

/** Tira a parte que se repete ("abcabcabc" vira "abc"). */
function semRepeticao(s: string): string {
  for (let tam = 1; tam <= s.length / 2; tam++) {
    const pedaco = s.slice(0, tam);
    if (pedaco.repeat(Math.ceil(s.length / tam)).startsWith(s)) return pedaco;
  }
  return s;
}

const ehTrechoDeSequencia = (s: string) =>
  SEQUENCIAS.some((seq) => seq.includes(s) || [...seq].reverse().join("").includes(s));

/**
 * Motivo para recusar uma senha previsível, ou `null`. `email` entra quando a tela tem: a senha não
 * pode conter o começo do e-mail.
 */
export function fraquezaDaSenha(senha: string, contexto: { email?: string } = {}): string | null {
  const s = normalizar(senha).replace(/\s+/g, "");
  if (new Set(s).size < 4) return "Senha muito repetitiva. Use uma frase com palavras diferentes.";
  const base = semRepeticao(s);
  if (ehTrechoDeSequencia(base) || ehTrechoDeSequencia(s.slice(0, 10))) {
    return "Senha muito previsível (sequência de teclado ou de números). Use uma frase.";
  }
  const semPalavras = PALAVRAS_DO_CONTEXTO.reduce((acc, p) => acc.split(p).join(""), s).replace(/[^a-z]/g, "");
  if (semPalavras.length < 4) return "Senha muito previsível. Use uma frase que só você conheça.";
  const usuario = normalizar(contexto.email?.split("@")[0] ?? "").replace(/[^a-z0-9]/g, "");
  if (usuario.length >= 4 && s.replace(/[^a-z0-9]/g, "").includes(usuario)) return "A senha não pode conter o seu e-mail.";
  return null;
}

/** Senha nova (cadastro, redefinição, troca). O login não usa isto: contas antigas com senha curta
 *  continuam entrando, e a régua vale na próxima troca. */
export const senhaNovaSchema = z
  .string()
  .min(SENHA_MIN, `A senha deve ter ao menos ${SENHA_MIN} caracteres. Uma frase funciona bem.`)
  .refine((s) => new TextEncoder().encode(s).length <= SENHA_MAX_BYTES, "Senha muito longa: use até 72 caracteres sem acento.")
  .superRefine((s, ctx) => {
    const motivo = fraquezaDaSenha(s);
    if (motivo) ctx.addIssue({ code: z.ZodIssueCode.custom, message: motivo });
  });
